import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@attmos/flowstack-ui";

import { useBudget, type BudgetSummary, type CostLineItem, type TileSpend } from "@/api/budget";
import { cn } from "@/lib/utils";

import { AddFunds } from "./AddFunds";

export function BudgetPage() {
  const { data, isLoading, isError } = useBudget();

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" className="p-6 text-sm text-muted-foreground">
        Loading budget…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Card elevation="raised">
        <CardHeader>
          <CardTitle>Could not load budget</CardTitle>
          <CardDescription>The FlowStack server returned an error.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">Budget</h1>
          {/* Estimate badge is driven by the server's pricing_basis
              (U0.3, C0.5): rendered only for "placeholder-estimate";
              absent field (older server) ⇒ no badge. */}
          {data.pricing_basis === "placeholder-estimate" && (
            <span className="label-micro rounded-sm bg-muted px-1.5 py-0.5 text-muted-foreground">
              estimate
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Tenant-scoped cost ledger per anchor §3.7. Period{" "}
          <span className="font-mono">{data.period_start}</span> to{" "}
          <span className="font-mono">{data.period_end}</span>.
        </p>
      </div>

      <HeadlineNumbers data={data} />

      <WalletAndLimits data={data} />

      <section aria-labelledby="line-items-heading">
        <h2 id="line-items-heading" className="mb-2 text-lg font-semibold">
          Current month by category
        </h2>
        <LineItemsTable items={data.line_items} />
      </section>

      <section aria-labelledby="top-tiles-heading">
        <h2 id="top-tiles-heading" className="mb-2 text-lg font-semibold">
          Top spending tiles
        </h2>
        {data.top_tiles.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tile runs yet this month.</p>
        ) : (
          <TopTilesTable tiles={data.top_tiles} />
        )}
      </section>
    </div>
  );
}

function HeadlineNumbers({ data }: { data: BudgetSummary }) {
  // Money arrives as exact decimal strings; Number() is for display only.
  // `!= null` throughout: an older server omits a field rather than nulling it.
  // The cap is the one remaining_cap_usd was computed against, so take it
  // from limits when sent; monthly_budget_usd is the pre-U2.3 fallback
  // (R-2171-16, R-2171-17).
  const capField = data.limits ? data.limits.monthly_cap_usd : data.monthly_budget_usd;
  const cap = capField != null ? Number(capField) : null;
  const remaining =
    data.limits?.remaining_cap_usd != null ? Number(data.limits.remaining_cap_usd) : null;
  const charged =
    data.limits?.charged_this_month_usd != null ? Number(data.limits.charged_this_month_usd) : null;
  // Cap use is what submission enforces: charged plus held this month, i.e.
  // the cap less remaining_cap_usd. Never current_month_usd, the cost-ledger
  // estimate, which can disagree with it either way (C-058-8).
  const pctOfBudget =
    cap !== null && cap > 0 && remaining !== null
      ? Math.round(((cap - remaining) / cap) * 100)
      : null;
  // Negative once charges and holds pass the cap: spend from before a cap was
  // set or lowered, a $0 cap included (R-2171-3, R-2171-10).
  const overCap = remaining !== null && remaining < 0;
  const capCaptionClass = cn(
    "mt-1 text-xs",
    overCap ? "font-medium text-destructive" : "text-muted-foreground"
  );

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <StatCard label="Current month">
        <p className="text-2xl font-semibold tabular-nums">{formatUsd(data.current_month_usd)}</p>
        {charged !== null && (
          <p className="mt-1 text-xs text-muted-foreground">
            {formatUsd(charged)} charged this month
          </p>
        )}
        {/* Show the X% caption only when we have a usable cap to
            divide by. Gating on pctOfBudget !== null (rather than the
            raw monthly_budget_usd !== null) closes the
            monthly_budget_usd === 0 edge case — rendering
            "% of $0.00 cap" would be confusing copy. PR-23 r1
            addressing Rupert's review on #199. A server older than
            U2.3 sends no limits, so there is no cap use to show: the
            caption then names the cap alone (R-2171-15, R-2171-18). */}
        {pctOfBudget !== null ? (
          <p className={capCaptionClass}>
            {pctOfBudget}% of {formatUsd(cap!)} cap charged or held
          </p>
        ) : cap === null ? (
          <p className={capCaptionClass}>No monthly cap set</p>
        ) : cap === 0 && remaining !== null ? (
          <p className={capCaptionClass}>
            {overCap ? "Over the $0 monthly cap" : "Monthly cap is $0: no spending"}
          </p>
        ) : (
          <p className={capCaptionClass}>{formatUsd(cap)} monthly cap</p>
        )}
      </StatCard>
      <StatCard label="Total to date">
        <p className="text-2xl font-semibold tabular-nums">{formatUsd(data.total_usd)}</p>
      </StatCard>
      <StatCard label="Monthly budget">
        {/* C1.6: a null cap renders "No cap", never "$0.00" or null.
            The cap is enforced at each Tile submission (U2.3, fs#1216);
            flow runs are not held or charged yet. */}
        <p className="text-2xl font-semibold tabular-nums">
          {cap !== null ? formatUsd(cap) : "No cap"}
        </p>
        <p
          className={cn(
            "mt-1 text-xs",
            overCap ? "font-medium text-destructive" : "text-muted-foreground"
          )}
        >
          {remaining !== null
            ? overCap
              ? `Over cap by ${formatUsd(-remaining)}`
              : `${formatUsd(remaining)} left this month`
            : cap !== null
              ? "Enforced at Tile submission"
              : "Unbounded"}
        </p>
      </StatCard>
    </div>
  );
}

function WalletAndLimits({ data }: { data: BudgetSummary }) {
  const { wallet, limits } = data;
  if (!wallet) return null;
  return (
    <section aria-labelledby="wallet-heading" className="space-y-2">
      <h2 id="wallet-heading" className="text-lg font-semibold">
        Wallet
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="Available">
          <p className="text-2xl font-semibold tabular-nums">
            {formatUsd(Number(wallet.available_usd))}
          </p>
        </StatCard>
        <StatCard label="Held">
          <p className="text-2xl font-semibold tabular-nums">
            {formatUsd(Number(wallet.held_usd))}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Reserved for runs in flight</p>
        </StatCard>
        <StatCard label="Balance">
          <p className="text-2xl font-semibold tabular-nums">
            {formatUsd(Number(wallet.balance_usd))}
          </p>
        </StatCard>
      </div>
      {limits && (
        <p className="text-sm text-muted-foreground">
          Per-run cap:{" "}
          {limits.per_run_cap_usd != null ? formatUsd(Number(limits.per_run_cap_usd)) : "No cap"}
          {" · "}
          Concurrent runs:{" "}
          {limits.max_concurrent_runs != null ? limits.max_concurrent_runs : "No limit"}
        </p>
      )}
      <AddFunds />
    </section>
  );
}

function StatCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // Was a hand-rolled `rounded-md border bg-card p-card` — the second card
    // recipe, in the third spelling. `rounded-md` -> `rounded-lg` is the only
    // pixel it moves. flowstack#1055.
    <Card className="p-card">
      <p className="label-micro text-muted-foreground">{label}</p>
      <div className="mt-1">{children}</div>
    </Card>
  );
}

function LineItemsTable({ items }: { items: CostLineItem[] }) {
  return (
    <div className="overflow-hidden rounded-md border">
      <Table>
        <TableHeader surface="tinted">
          <TableRow>
            <TableHead>Category</TableHead>
            <TableHead align="right">Current month</TableHead>
            <TableHead align="right">Total to date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.category}>
              <TableCell className="font-mono text-xs">{it.category}</TableCell>
              <TableCell align="right" className="text-xs tabular-nums">
                {formatUsd(it.current_month_usd)}
              </TableCell>
              <TableCell align="right" className="text-xs tabular-nums">
                {formatUsd(it.total_usd)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function TopTilesTable({ tiles }: { tiles: TileSpend[] }) {
  return (
    <div className="overflow-hidden rounded-md border">
      <Table>
        <TableHeader surface="tinted">
          <TableRow>
            <TableHead>Tile</TableHead>
            <TableHead align="right">Runs</TableHead>
            <TableHead align="right">Current month</TableHead>
            <TableHead align="right">Total to date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tiles.map((t) => (
            <TableRow key={t.tile_name}>
              <TableCell className="font-mono text-xs">{t.tile_name}</TableCell>
              <TableCell align="right" className="text-xs tabular-nums">
                {t.run_count}
              </TableCell>
              <TableCell align="right" className="text-xs tabular-nums">
                {formatUsd(t.current_month_usd)}
              </TableCell>
              <TableCell align="right" className="text-xs tabular-nums">
                {formatUsd(t.total_usd)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function formatUsd(amount: number): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
